import { getContext } from '@fiverr-private/fiverr_context';
import { resultInto } from '../NotificationsDrawer/utils';
import { DEFAULT_VALUE } from '../NotificationsDrawer/enums/consts';
import types from './types';
import { detectLinks } from './utils/detectLinks';
import { GENERIC_MESSAGE_PLACEHOLDER } from './constants';

export interface NotificationItem {
    type?: string;
    subType?: string;
    sub_type?: string;
    profilePicture?: string;
    profilePictureUrl?: string;
    username?: string;
    unreadCount?: number;
    unread_count?: number;
    msg_type?: string;
    msg_params?: {
        seller?: string;
        user?: string;
        buyer?: string;
        icon?: string;
        full_url?: string;
        url?: string;
        content?: string;
        images?: {
            user?: string;
            profile_image?: string;
        };
    };
    images?: {
        user?: string;
        gig?: string;
    };
    isRead?: boolean;
    is_read?: boolean;
    online?: boolean;
    is_online?: boolean;
    url?: string;
    previewImage?: string;
    userMessage?: string;
    params?: {
        message_content?: string;
        content?: string;
    };
    preview_text?: string;
    content?: string;
    showEncourageSellerReply?: boolean;
    show_encourage_seller_reply?: boolean;
    encourageSellerReplyGroup?: string;
    encourage_seller_reply_group?: string;
    senderId?: string;
    sender_id?: string;
    updatedAt?: string;
    updated_at?: string;
    last_sender_id?: number;
    [key: string]: any;
}

/**
 * Represents the default notification subtype in case it is missing.
 * Each pinned flash notification should have a specific subtype, thus
 * when we do not have one, it should represent a conversation notification.
 *
 * @type {string}
 */
const DEFAULT_SUB_TYPE = 'conversation';

const isConversation = (item: NotificationItem): boolean => item.type === 'inbox';

const formatSubType = (item: NotificationItem): NotificationItem => {
    if (isConversation(item)) {
        item.subType = DEFAULT_SUB_TYPE;
    } else {
        item.subType = item.sub_type;
    }

    return item;
};

const formatProfilePictureUrl = (item: NotificationItem): NotificationItem => {
    if (!item.profilePicture) {
        return item;
    }

    const re = /https?:\/\/[^(\s|"|)]*/gi;

    const profilePictureUrl = item.profilePicture.match(re)?.[0];

    if (profilePictureUrl) {
        item.profilePictureUrl = profilePictureUrl;
    }

    return item;
};

// Yes, the data structure is nasty. This is just me trying to flatten it down.
const formatUsername = (item: NotificationItem): NotificationItem =>
    resultInto(item, ['username', 'msg_params.seller', 'msg_params.user', 'msg_params.buyer']);
const formatUnreadCount = (item: NotificationItem): NotificationItem =>
    resultInto(item, ['unreadCount', 'unread_count']);
const formatType = (item: NotificationItem): NotificationItem => resultInto(item, ['type', 'msg_type']);
const formatProfilePicture = (item: NotificationItem): NotificationItem =>
    resultInto(item, ['profilePicture', 'images.user', 'msg_params.images.user', 'msg_params.images.profile_image']);
const formatIcon = (item: NotificationItem): NotificationItem =>
    resultInto(item, ['msg_params.icon'], item.subType ? types[item.subType] : undefined);
const formatIsRead = (item: NotificationItem): NotificationItem => resultInto(item, ['isRead', 'is_read']);
const formatOnline = (item: NotificationItem): NotificationItem => resultInto(item, ['online', 'is_online']);
const formatUrl = (item: NotificationItem): NotificationItem =>
    resultInto(item, ['url', 'msg_params.full_url', 'msg_params.url'], '#!');
const formatPreviewImage = (item: NotificationItem): NotificationItem =>
    resultInto(item, ['previewImage', 'images.gig']);
const formatUserMessage = (item: NotificationItem): NotificationItem => {
    const formattedItem = resultInto(item, [
        'userMessage',
        'params.message_content',
        'preview_text',
    ]) as NotificationItem;

    const { userId } = getContext();
    const isOwnMessage = formattedItem.last_sender_id === userId;

    // For non-own messages, replace content with a generic placeholder if it contains links
    if (
        !isOwnMessage &&
        formattedItem.userMessage &&
        typeof formattedItem.userMessage === 'string' &&
        detectLinks(formattedItem.userMessage)
    ) {
        formattedItem.userMessage = GENERIC_MESSAGE_PLACEHOLDER;
    }

    return formattedItem;
};
const formatContent = (item: NotificationItem): NotificationItem =>
    isConversation(item) ? item : resultInto(item, ['content', 'params.content', 'msg_params.content'], DEFAULT_VALUE);
const formatShowEncourageSellerReply = (item: NotificationItem): NotificationItem =>
    resultInto(item, ['showEncourageSellerReply', 'show_encourage_seller_reply']);
const formatEncourageSellerReplyGroup = (item: NotificationItem): NotificationItem =>
    resultInto(item, ['encourageSellerReplyGroup', 'encourage_seller_reply_group']);
const formatSenderId = (item: NotificationItem): NotificationItem =>
    resultInto(item, ['senderId', 'sender_id', 'sender.id']);
const formatUpdatedAt = (item: NotificationItem): NotificationItem => resultInto(item, ['updatedAt', 'updated_at']);

const formatItem = (item: NotificationItem): NotificationItem =>
    [
        formatUsername,
        formatUnreadCount,
        formatType,
        formatSubType,
        formatProfilePicture,
        formatIcon,
        formatIsRead,
        formatOnline,
        formatUrl,
        formatPreviewImage,
        formatSenderId,
        formatUserMessage,
        formatContent,
        formatShowEncourageSellerReply,
        formatEncourageSellerReplyGroup,
        formatUpdatedAt,
        formatProfilePictureUrl,
    ].reduce((item, fn) => fn(item), item);

export default formatItem;
